import Cookies from "js-cookie";
import axiosInstance from "../../api/axiosInstance";

/** userId cookie is stored with JSON.stringify on login. */
const readLoggedInUserId = () => {
    const raw = Cookies.get("userId");
    if (raw == null || raw === "") return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed == null || parsed === "") return null;
        const asNumber = Number(parsed);
        return Number.isFinite(asNumber) ? asNumber : parsed;
    } catch {
        const asNumber = Number(raw);
        return Number.isFinite(asNumber) ? asNumber : raw;
    }
};

export class BillingError extends Error {
    constructor(message, status) {
        super(message);
        this.name = "BillingError";
        this.status = status;
    }
}

const messageFrom = (err, fallback) => {
    const data = err?.response?.data;
    if (data?.errors) {
        const msgs = Object.values(data.errors).flat().filter(Boolean);
        if (msgs.length) return msgs.join(", ");
    }
    return data?.message || data?.error || err?.message || fallback;
};

const request = async (fn, fallback) => {
    try {
        const res = await fn();
        const body = res?.data;
        if (body?.success === false) {
            throw new BillingError(body.message || fallback, res?.status);
        }
        return body;
    } catch (err) {
        if (err instanceof BillingError) throw err;
        throw new BillingError(messageFrom(err, fallback), err?.response?.status);
    }
};

export const extractPage = (body) => {
    const payload = body?.data;
    if (payload && Array.isArray(payload.data)) {
        const pageSize = payload.per_page || payload.data.length || 1;
        const total = payload.total ?? payload.data.length;
        return {
            rows: payload.data,
            page: payload.current_page || 1,
            lastPage: payload.last_page || Math.max(1, Math.ceil(total / pageSize)),
            total,
            totalCredit: Number(payload.total_credit ?? 0),
            totalDebit: Number(payload.total_debit ?? 0),
        };
    }
    if (Array.isArray(payload)) {
        const meta = body?.meta || {};
        return {
            rows: payload,
            page: meta.current_page || 1,
            lastPage: meta.last_page || 1,
            total: meta.total ?? payload.length,
            totalCredit: Number(meta.total_credit ?? 0),
            totalDebit: Number(meta.total_debit ?? 0),
        };
    }
    return { rows: [], page: 1, lastPage: 1, total: 0, totalCredit: 0, totalDebit: 0 };
};

export const getWallet = () =>
    request(() => axiosInstance.get("/billing/wallet"), "Could not load wallet").then((body) => {
        const data = body?.data ?? {};
        return {
            user_id: data.user_id,
            wallet_user_id: data.wallet_user_id,
            billing_type: data.billing_type ?? null,
            current_credit: Number(data.current_credit ?? 0),
        };
    });

export const listTransactions = (params) =>
    request(
        () => axiosInstance.get("/billing/transactions", { params }),
        "Could not load the ledger"
    ).then(extractPage);

export const listTopups = (params) =>
    request(
        () => axiosInstance.get("/billing/topups", { params }),
        "Could not load top-ups"
    ).then(extractPage);

/** Create-order response fields are on the body root (`order_id`, `amount_in_rupees`, …). */
export const createCreditPayment = (amountRupees) => {
    const payload = { amount: amountRupees, purpose: "credit_topup" };
    const userId = readLoggedInUserId();
    // Payment route allows unauthenticated create when user_id is sent.
    if (userId != null) payload.user_id = userId;
    return request(
        () => axiosInstance.post("/payment", payload),
        "Could not start the payment"
    );
};

export const confirmCreditPayment = ({ paymentId, orderId, signature }) =>
    request(
        () => axiosInstance.post("/payment/success", {
            payment_id: paymentId,
            order_id: orderId,
            signature,
        }),
        "Could not confirm the payment"
    );

/** User-facing messages for create/verify payment status codes from the API doc. */
export const paymentErrorMessage = (err, fallback = "Payment could not be completed") => {
    const status = err?.status;
    const apiMessage = err?.message;
    if (status === 401) return apiMessage || "Please sign in again to buy credits.";
    if (status === 403) return apiMessage || "Only the account admin can buy credits.";
    if (status === 404) return apiMessage || "That account was not found.";
    if (status === 422) {
        return apiMessage || "This amount is not allowed, or the account is not on a credit plan.";
    }
    if (status === 400) return apiMessage || "Razorpay could not create the order.";
    if (status === 500) {
        return apiMessage || "Payment could not be started. Check Razorpay keys on the server.";
    }
    return apiMessage || fallback;
};
